import { FlexColumn, FlexResponsive } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Tab, Tabs } from 'baseui/tabs-motion';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useStyles } from '../../hooks/useStyles';
import { Asset, CurrentState } from '../../types/asset';
import { MinimalDevice } from '../../types/device';
import Map from '../map/Map';
import CurrentStateDevice from '../shared/devices/CurrentStateDevice';
import CurrentStateInfo from '../shared/devices/CurrentStateInfo';
import CurrentStateLocation from '../shared/devices/CurrentStateLocation';

type Props = Readonly<{
  asset: Asset;
}>;

function AssetCurrentStateWrapper({ asset }: Props) {
  const { css, theme } = useStyles();
  const { isSmall } = useBreakpoints();
  const [activeTab, setActiveTab] = useState<React.Key>();

  const secondaries = useMemo(() => {
    return asset.secondariesCS?.reduce<{ currentState: CurrentState; device: MinimalDevice }[]>((acc, secondaryCs) => {
      const secondaryDevice = asset.secondaries?.find((secondary) => secondary.ident === secondaryCs.ident);

      if (secondaryDevice) {
        acc.push({
          currentState: secondaryCs,
          device: secondaryDevice,
        });
      }

      return acc;
    }, []);
  }, [asset.secondaries, asset.secondariesCS]);

  const records = useMemo(() => {
    if (asset.cs && asset.device) {
      const mainDevice = {
        currentState: asset.cs,
        device: asset.device,
      };
      return secondaries ? [mainDevice, ...secondaries] : [mainDevice];
    }
  }, [secondaries, asset.cs, asset.device]);

  useEffect(() => {
    setActiveTab(records?.[0]?.device.ident);
  }, [records]);

  return (
    <FlexColumn gap={0}>
      {(asset.cs?.geoReference || !!asset.secondariesCS?.length) && (
        <Block
          className={css({
            width: '100%',
          })}
        >
          <Map
            fullWindow={false}
            minHeight={isSmall ? '150px' : '250px'}
            minWidth="100%"
          />
        </Block>
      )}
      {records && (
        <Tabs
          activeKey={activeTab}
          onChange={({ activeKey }) => {
            setActiveTab(activeKey);
          }}
          overrides={{
            Root: {
              style: () => ({
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }),
            },
            TabBorder: {
              style: { flexShrink: 0 },
            },
          }}
        >
          {records.map((record, index) => (
            <Tab
              title={
                <LabelSmall>
                  {record.device.ident}{' '}
                  {index === 0 && <span style={{ color: theme.colors.contentSecondary }}>(Primario)</span>}
                </LabelSmall>
              }
              key={record.device.ident}
            >
              <FlexResponsive breakAt="extraLarge">
                <FlexResponsive
                  classNames={css({ flex: 4 })}
                  breakAt="medium"
                >
                  {record.device && (
                    <CurrentStateDevice
                      model={record.currentState?.deviceModel || '-'}
                      device={record.device}
                    />
                  )}
                  <CurrentStateInfo
                    currentState={record.currentState}
                    isEngineStall={asset.isEngineStall}
                  />
                </FlexResponsive>
                <CurrentStateLocation currentState={record.currentState} />
              </FlexResponsive>
            </Tab>
          ))}
        </Tabs>
      )}
    </FlexColumn>
  );
}

export default AssetCurrentStateWrapper;
